import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { useComponentTranslation } from "@/services/i18n";
import { FaArrowLeft, FaBriefcase, FaCopy } from "react-icons/fa";
import { CANDIDATE_URL } from "@/config";
import API from "@/services/api";
import PublishToggle from "./components/publish-toggle";
import toast from "react-hot-toast";

const translations = {
  en: {
    title: "Jobego",
    share_button: "View on Jobego",
    instructions: "Share this job posting on Jobego platform.",
    back: "Back to channels",
    publish: "Publish",
    unpublish: "Unpublish",
    post_published: "Job published successfully",
    post_unpublished: "Job unpublished successfully",
    post_not_enabled: "Failed to publish job",
    loading: "Loading...",
    link_copied: "Link copied to clipboard",
  },
  fr: {
    title: "Jobego",
    share_button: "Voir sur Jobego",
    instructions: "Partagez cette offre d'emploi sur la plateforme Jobego.",
    back: "Retour aux canaux",
    publish: "Publier",
    unpublish: "Dépublier",
    post_published: "Offre publiée avec succès",
    post_unpublished: "Offre dépubliée avec succès",
    post_not_enabled: "Échec de la publication",
    loading: "Chargement...",
    link_copied: "Lien copié dans le presse-papiers",
  },
};

export default function JobegoChannel({ job, setJob, posts = [], setPosts }) {
  const { t } = useComponentTranslation(translations);
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const channelSlug = "jobego";

  useEffect(() => {
    fetchChannel();
  }, []);

  const fetchChannel = async () => {
    try {
      setLoading(true);
      const channelResponse = await API.post(`/channel/search`, {
        slug: channelSlug,
        limit: 1,
      });
      if (channelResponse.ok && channelResponse.data?.[0]) {
        setChannel(channelResponse.data[0]);
      }
    } catch (e) {
      console.error("Error loading channel:", error);
    } finally {
      setLoading(false);
    }
  };

  // Find job post from posts array
  const jobPost = channel ? posts.find((post) => post.channel_id === channel._id) : null;

  const shareLink = useMemo(() => {
    if (!job?.organization_slug || !jobPost?._id) return "";
    return `https://jobego.co/companies/${job.organization_slug}/jobs/${jobPost._id}`;
  }, [job, jobPost]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success(t("link_copied"));
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-gray-600">{t("loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button */}
      <button onClick={() => navigate("..")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <FaArrowLeft className="w-4 h-4" />
        {t("back")}
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          {channel?.logo ? (
            <img src={channel.logo} alt={channel.name} className="w-16 h-16 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center shrink-0">
              <FaBriefcase className="w-8 h-8 text-white" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-bold text-gray-900">{channel?.name || t("title")}</h3>
              {channel && <PublishToggle job={job} channel={channel} posts={posts} setPosts={setPosts} />}
            </div>
            <p className="text-sm text-gray-600 mb-6">{channel?.description || t("instructions")}</p>
            {jobPost && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2">{t("instructions")}</p>
                <div className="flex items-center gap-2 bg-white border border-gray-200 rounded px-3 py-2">
                  <code className="text-sm text-gray-600 flex-1 truncate">{shareLink}</code>
                  <button onClick={handleCopyLink} className="p-1.5 hover:bg-gray-100 rounded transition-colors" title={t("copy_link")}>
                    <FaCopy className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
